import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  notification,
} from "antd";
import { LuX } from "react-icons/lu";
import { CgAddR } from "react-icons/cg";
import {
  useCreateCampaignMutation,
  useGetOrganizationsQuery,
  useUpdateCampaignMutation,
} from "../../store/api/apiSlice";
import { getCampaignStatusFromForms } from "../../utils/campaigns";

const CreateCampaignModal = ({
  visible,
  onClose,
  organizationId = null,
  editData = null,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [selectedOrganizationId, setSelectedOrganizationId] = useState();
  const [createCampaign, { isLoading: isCreating }] =
    useCreateCampaignMutation();
  const [updateCampaign, { isLoading: isUpdating }] =
    useUpdateCampaignMutation();
  const { data: organizations } = useGetOrganizationsQuery();

  const [availableForms, setAvailableForms] = useState([]);

  useEffect(() => {
    if (!visible) {
      setAvailableForms([]);
      setSelectedOrganizationId(undefined);
    }
  }, [visible]);

  // Auto-set organization when organizationId is provided (from organization view)
  useEffect(() => {
    if (visible && organizationId && !editData && organizations?.data) {
      // Find the organization data using raw API data
      const selectedOrg = organizations.data.find(
        (org) => org._id === organizationId
      );
      if (selectedOrg) {
        form.setFieldValue("organization", organizationId);
        setSelectedOrganizationId(organizationId);
        setAvailableForms(selectedOrg.forms || []);
      }
    }
  }, [visible, organizationId, editData, organizations, form]);

  useEffect(() => {
    if (visible && editData && organizations?.data) {
      const formData = {
        title: editData.title,
        description: editData.description,
        organization: editData?.organization?._id,
        forms: (editData.forms || []).map((form) => form._id),
      };

      form.setFieldsValue(formData);
      setSelectedOrganizationId(formData.organization);

      // Set available forms from the selected organization
      if (editData.organization) {
        const selectedOrg = organizations?.data?.find(
          (org) => org._id === editData.organization._id
        );
        if (selectedOrg?.forms) {
          setAvailableForms(selectedOrg.forms);
        }
      }
    }
  }, [visible, editData, form, organizations]);

  const handleOrganizationChange = (orgId) => {
    setSelectedOrganizationId(orgId);
    form.setFieldValue("forms", []);
    if (organizations?.data) {
      const selectedOrg = organizations.data.find((org) => org._id === orgId);
      setAvailableForms(selectedOrg?.forms || []);
    }
  };
  const handleSubmit = async (e) => {
    try {
      const values = await form.validateFields();
      const formById = new Map();

      availableForms.forEach((item) => {
        if (item?._id) {
          formById.set(item._id, item);
        }
      });

      (editData?.forms || []).forEach((item) => {
        if (item?._id && !formById.has(item._id)) {
          formById.set(item._id, item);
        }
      });

      const selectedForms = (values.forms || [])
        .map((id) => formById.get(id))
        .filter(Boolean);

      const payload = {
        title: values.title,
        description: values.description,
        organization: values.organization || null,
        forms: values.forms || [],
        status: getCampaignStatusFromForms(selectedForms),
      };

      if (editData) {
        await updateCampaign({
          id: editData._id,
          data: payload,
        }).unwrap();
        notification.success({
          message: "Campaign Updated",
          description: "Campaign has been updated successfully",
        });
      } else {
        await createCampaign(payload).unwrap();
        notification.success({
          message: "Campaign Created",
          description: "New campaign has been created successfully",
        });
      }

      form.resetFields();
      onSuccess?.();
      onClose();
    } catch (error) {
      notification.error({
        message: "Error",
        description:
          error.message ||
          `Failed to ${editData ? "update" : "create"} campaign`,
      });
    }
  };

  // Prevent accidental form submission when pressing enter
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={() => {
        form.resetFields();
        onClose();
      }}
      destroyOnClose
      afterClose={() => {
        setAvailableForms([]);
      }}
      width={600}
      closeIcon={
        <div className="bg-secondary h-8 w-8 flex justify-center items-center rounded-full">
          <LuX className="text-red-400" size={20} />
        </div>
      }
      title={
        <h3 className="text-xl font-semibold">
          {editData ? "Edit Campaign" : "Create Campaign"}
        </h3>
      }
      className="campaign-modal"
    >
      <Form
        form={form}
        layout="vertical"
        className="mt-6"
        onKeyPress={handleKeyPress}
        onFinish={handleSubmit}
      >
        <Form.Item
          label="Campaign Title"
          name="title"
          rules={[
            {
              required: true,
              message: "Please enter campaign title",
            },
          ]}
        >
          <Input
            placeholder="A descriptive name for the campaign (e.g., Feedback Drive 2024)."
            className="h-11 rounded-xl"
            maxLength={100}
          />
        </Form.Item>

        <Form.Item
          label="Description"
          name="description"
          rules={[
            {
              required: true,
              message: "Please enter campaign description",
            },
          ]}
        >
          <Input.TextArea
            placeholder="Explain the purpose or objectives of this campaign..."
            className="rounded-xl"
            rows={4}
            maxLength={500}
            showCount
          />
        </Form.Item>

        <div className="grid sm:grid-cols-2 grid-cols-1 gap-4">
          <Form.Item
            label={
              <div className="flex items-center gap-2">
                <span>Select Organisation</span>
                {/* {organizationId && (
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    Pre-selected from organisation
                    view
                  </span>
                )} */}
              </div>
            }
            name="organization"
            className="mb-0"
            rules={[
              {
                required: true,
                message: "Please select Organisation",
              },
            ]}
          >
            <Select
              placeholder="Select organisation"
              className="w-full h-11 rounded-xl"
              onChange={handleOrganizationChange}
              options={
                organizations?.data?.map((org) => ({
                  value: org._id,
                  label: org.name,
                })) || []
              }
              showSearch
              disabled={!!organizationId && !editData}
              filterOption={(input, option) =>
                (option?.label?.toLowerCase() || "").includes(
                  input.toLowerCase()
                )
              }
              notFoundContent="No Organisations found"
            />
          </Form.Item>

          <Form.Item
            label="Select Forms"
            name="forms"
            rules={[
              {
                required: false,
                message: "Please select at least one form",
              },
            ]}
          >
            <Select
              placeholder="Select forms"
              className="w-full h-11 rounded-xl"
              mode="multiple"
              disabled={!selectedOrganizationId}
              options={availableForms.map((formItem) => ({
                value: formItem._id,
                label: formItem.title,
              }))}
              showSearch
              filterOption={(input, option) =>
                (option?.label?.toLowerCase() || "").includes(
                  input.toLowerCase()
                )
              }
              maxTagCount={2}
              notFoundContent={
                !selectedOrganizationId
                  ? "Please select an organisation first"
                  : "No forms available"
              }
            />
          </Form.Item>
        </div>

        <Button
          htmlType="submit"
          loading={isCreating || isUpdating}
          className="w-full h-11 bg-primary text-white rounded-xl font-medium mt-4 flex items-center justify-center gap-2"
        >
          <CgAddR className="text-lg" />{" "}
          {editData ? "Update Campaign" : "Create Campaign"}
        </Button>
      </Form>
    </Modal>
  );
};

export default CreateCampaignModal;
